import { LAYER_GROUPS } from '../map/layerGroups.js';
import { POI_SPLIT } from '../map/style.js';
import { POI_THEMES, OTHER_THEME, themeOf, classLabel } from '../map/poiCategories.js';
import { toggleRow, expandButton, counter } from './toggles.js';

const ALL_KNOWN_CLASSES = POI_THEMES.flatMap((t) => t.classes);

/**
 * Hintergründe. `suppress` nennt die Ebenengruppen, die unter diesem
 * Hintergrund keinen Sinn ergeben – die Haken bleiben stehen, wirken aber erst
 * wieder, wenn man zurückschaltet.
 */
const BASEMAPS = [
  { id: 'farbig', label: 'Farbig', palette: 'farbig', raster: null, suppress: [] },
  // Das Kartenbild vor dem Abgleich mit swisstopos Base Map.
  { id: 'classic', label: 'Classic', palette: 'classic', raster: null, suppress: [] },
  { id: 'hell', label: 'Hell', palette: 'hell', raster: null, suppress: [] },
  { id: 'sw', label: 'Schwarzweiss', palette: 'sw', raster: null, suppress: [] },
  {
    id: 'luftbild',
    label: 'Luftbild',
    // Eigene Palette: identisch zu "farbig", aber mit Halo um die Schrift -
    // auf einem Foto traegt sonst nichts die Beschriftung.
    palette: 'luftbild',
    raster: 'bg-aerial',
    suppress: ['relief', 'vegetation', 'surface', 'buildings', 'aeroway', 'water-lakes']
  }
];
const THEMES = [...POI_THEMES, OTHER_THEME];

/** layerId -> { parent, child } für die Sichtbarkeitsregel */
function buildLayerIndex() {
  const idx = new Map();
  for (const g of LAYER_GROUPS) {
    for (const l of g.layers) idx.set(l, { parent: g.id, child: null });
    for (const c of g.children || []) {
      for (const l of c.layers) idx.set(l, { parent: g.id, child: c.id });
    }
  }
  return idx;
}

export function createFilterPanel({ map, onChange, onPalette, onKategorieWeg }) {
  const layerIndex = buildLayerIndex();
  const inputs = new Map(); // groupId -> input
  const themeInputs = new Map();
  const classInputs = new Map();
  const classCounters = new Map();
  const themeCounters = new Map();

  const groupRows = new Map();
  const state = defaultState();

  function defaultState() {
    const groups = {};
    for (const g of LAYER_GROUPS) {
      groups[g.id] = g.default !== false;
      for (const c of g.children || []) groups[c.id] = c.default !== false;
    }
    const poiThemes = {};
    for (const t of THEMES) poiThemes[t.id] = true;
    return { groups, poiThemes, poiClasses: {}, poiName: '', basemap: 'farbig' };
  }

  /* ----------------------------------------------------- Karte aktualisieren */

  function currentBasemap() {
    return BASEMAPS.find((b) => b.id === state.basemap) || BASEMAPS[0];
  }

  function applyVisibility() {
    const base = currentBasemap();
    const muted = new Set(base.suppress);

    for (const b of BASEMAPS) {
      if (b.raster && map.getLayer(b.raster)) {
        map.setLayoutProperty(b.raster, 'visibility', b.id === base.id ? 'visible' : 'none');
      }
    }

    for (const [layerId, { parent, child }] of layerIndex) {
      if (!map.getLayer(layerId)) continue;
      const on =
        !muted.has(parent) &&
        !(child && muted.has(child)) &&
        state.groups[parent] !== false &&
        (child ? state.groups[child] !== false : true);
      map.setLayoutProperty(layerId, 'visibility', on ? 'visible' : 'none');
    }

    for (const [id, row] of groupRows) row.classList.toggle('is-muted', muted.has(id));
  }



  /**
   * Vorübergehende Einschränkung aus der Kategoriesuche. Sie liegt über den
   * Einstellungen des Filterfelds, verändert sie aber nicht - wer sie aufhebt,
   * bekommt genau das zurück, was vorher eingestellt war.
   */
  let kategorie = null;

  function setKategorie(k) {
    kategorie = k && k.classes?.length ? k : null;
    applyPoiFilter();
    zeigeKategorieHinweis();
  }

  function applyPoiFilter() {
    if (!map.getLayer('poi-symbol')) return;

    const blocked = new Set();
    for (const t of POI_THEMES) {
      if (state.poiThemes[t.id] === false) for (const c of t.classes) blocked.add(c);
    }
    for (const [cls, on] of Object.entries(state.poiClasses)) {
      if (on === false) blocked.add(cls);
      else blocked.delete(cls);
    }

    const parts = [['==', ['geometry-type'], 'Point']];
    if (blocked.size) parts.push(['!', ['in', ['get', 'class'], ['literal', [...blocked]]]]);
    if (state.poiThemes[OTHER_THEME.id] === false) {
      parts.push(['in', ['get', 'class'], ['literal', ALL_KNOWN_CLASSES]]);
    }
    if (kategorie) {
      parts.push(['in', ['get', 'class'], ['literal', kategorie.classes]]);
    }
    const needle = state.poiName.trim().toLowerCase();
    if (needle) {
      parts.push([
        'in',
        needle,
        // Beide Schreibweisen durchsuchbar: die Karte schreibt "Milano" an,
        // getippt wird vielleicht trotzdem "Mailand".
        ['downcase', ['concat',
          ['coalesce', ['get', 'name'], ''], ' ',
          ['coalesce', ['get', 'name:de'], ['get', 'name_de'], '']]]
      ]);
    }

    for (const [id, split] of Object.entries(POI_SPLIT)) {
      if (map.getLayer(id)) map.setFilter(id, ['all', ...parts, split]);
    }
  }

  /** Ein Hinweis mit Rücknahme - sonst wundert man sich, wo die Punkte sind. */
  function zeigeKategorieHinweis() {
    // `hosts` wird beim Einhängen gesetzt; vorher gibt es nichts zu zeigen.
    const ziel = hosts?.themeHost;
    const host = ziel?.parentElement;
    if (!host) return;
    host.querySelector('.kategoriehinweis')?.remove();
    if (!kategorie) return;
    const box = document.createElement('div');
    box.className = 'kategoriehinweis';
    const text = document.createElement('span');
    text.textContent = `Nur ${kategorie.label}`;
    const weg = document.createElement('button');
    weg.type = 'button';
    weg.className = 'linkbtn';
    weg.textContent = 'Alle zeigen';
    weg.addEventListener('click', () => { setKategorie(null); onKategorieWeg?.(); });
    box.append(text, weg);
    host.insertBefore(box, ziel);
  }

  function apply() {
    onPalette?.(currentBasemap().palette);
    applyVisibility();
    applyPoiFilter();
    onChange?.(state);
  }

  /** Nach einem Style-Wechsel sitzen wieder die Vorgaben - Zustand neu auftragen. */
  function reapply() {
    applyVisibility();
    applyPoiFilter();
  }

  /* ------------------------------------------------------------ Ebenengruppen */

  function syncParent(group) {
    const parentInput = inputs.get(group.id);
    const kids = (group.children || []).map((c) => inputs.get(c.id)).filter(Boolean);
    if (!parentInput || !kids.length) return;
    const on = kids.filter((k) => k.checked).length;
    parentInput.indeterminate = on > 0 && on < kids.length;
    parentInput.checked = on > 0;
    state.groups[group.id] = on > 0;
  }

  function renderGroups(host) {
    host.textContent = '';
    for (const g of LAYER_GROUPS) {
      const box = document.createElement('div');
      box.className = 'group';

      const { row, input } = toggleRow({
        id: g.id,
        label: g.label,
        note: g.note,
        checked: state.groups[g.id] !== false,
        onChange: (checked) => {
          state.groups[g.id] = checked;
          for (const c of g.children || []) {
            state.groups[c.id] = checked;
            const ci = inputs.get(c.id);
            if (ci) ci.checked = checked;
          }
          input.indeterminate = false;
          apply();
        }
      });
      inputs.set(g.id, input);

      let kidsBox;
      if (g.children?.length) {
        kidsBox = document.createElement('div');
        kidsBox.className = 'group__children';
        kidsBox.hidden = true;
        row.append(expandButton(false, (open) => { kidsBox.hidden = !open; }));

        for (const c of g.children) {
          const kid = toggleRow({
            id: c.id,
            label: c.label,
            swatch: c.swatch,
            checked: state.groups[c.id] !== false,
            onChange: (checked) => {
              state.groups[c.id] = checked;
              syncParent(g);
              apply();
            }
          });
          inputs.set(c.id, kid.input);
          kidsBox.append(kid.row);
        }
      }

      groupRows.set(g.id, row);
      box.append(row);
      if (kidsBox) box.append(kidsBox);
      host.append(box);
      syncParent(g);
    }
  }

  /* ---------------------------------------------------------------- POI-Teil */

  function renderThemes(host) {
    host.textContent = '';
    for (const t of THEMES) {
      const box = document.createElement('div');
      box.className = 'group';
      box.dataset.theme = t.id;

      const { row, input } = toggleRow({
        id: `theme-${t.id}`,
        label: t.label,
        dot: t.color,
        checked: state.poiThemes[t.id] !== false,
        onChange: (checked) => {
          state.poiThemes[t.id] = checked;
          for (const c of t.classes) {
            delete state.poiClasses[c];
            const ci = classInputs.get(`class-${c}`);
            if (ci) ci.checked = checked;
          }
          input.indeterminate = false;
          apply();
        }
      });
      themeInputs.set(t.id, input);

      const cnt = counter();
      themeCounters.set(t.id, cnt);
      row.append(cnt);

      const kidsBox = document.createElement('div');
      kidsBox.className = 'group__children';
      kidsBox.hidden = true;
      kidsBox.dataset.classHost = t.id;
      row.append(expandButton(false, (open) => { kidsBox.hidden = !open; }));

      box.append(row, kidsBox);
      host.append(box);
    }
  }

  function classEnabled(cls) {
    if (cls in state.poiClasses) return state.poiClasses[cls];
    return state.poiThemes[themeOf(cls)] !== false;
  }

  function syncThemeParent(themeId, classes) {
    const input = themeInputs.get(themeId);
    if (!input || !classes.length) return;
    const on = classes.filter(classEnabled).length;
    input.indeterminate = on > 0 && on < classes.length;
    input.checked = on > 0;
  }

  /** Klassenliste je Thema aus den geladenen Tiles aufbauen (mit Anzahl im Ausschnitt) */
  function refreshCounts() {
    if (!map.getLayer('poi-symbol')) return;
    let feats = [];
    try {
      feats = map.querySourceFeatures('osm', { sourceLayer: 'poi' });
    } catch {
      return;
    }
    const bounds = map.getBounds();
    const counts = new Map();
    for (const f of feats) {
      if (f.geometry?.type !== 'Point') continue;
      const [lng, lat] = f.geometry.coordinates;
      if (!bounds.contains([lng, lat])) continue;
      const cls = f.properties?.class;
      if (!cls) continue;
      counts.set(cls, (counts.get(cls) || 0) + 1);
    }

    for (const t of THEMES) {
      const host = document.querySelector(`[data-class-host="${t.id}"]`);
      if (!host) continue;

      const present = [...counts.keys()].filter((c) => themeOf(c) === t.id).sort((a, b) =>
        classLabel(a).localeCompare(classLabel(b), 'de')
      );
      const total = present.reduce((n, c) => n + counts.get(c), 0);
      const cnt = themeCounters.get(t.id);
      if (cnt) cnt.textContent = total ? String(total) : '';

      const wanted = present.join('|');
      if (host.dataset.signature !== wanted) {
        host.dataset.signature = wanted;
        host.textContent = '';
        for (const cls of present) {
          const kid = toggleRow({
            id: `class-${cls}`,
            label: classLabel(cls),
            checked: classEnabled(cls),
            onChange: (checked) => {
              state.poiClasses[cls] = checked;
              syncThemeParent(t.id, present);
              apply();
            }
          });
          classInputs.set(`class-${cls}`, kid.input);
          const c = counter();
          classCounters.set(cls, c);
          kid.row.append(c);
          host.append(kid.row);
        }
        if (!present.length) {
          const p = document.createElement('p');
          p.className = 'hint';
          p.textContent = 'Im Ausschnitt keine Objekte (ab Zoom 14 sichtbar).';
          host.append(p);
        }
      }

      for (const cls of present) {
        const c = classCounters.get(cls);
        if (c) c.textContent = String(counts.get(cls));
        const i = classInputs.get(`class-${cls}`);
        if (i) i.checked = classEnabled(cls);
      }
      syncThemeParent(t.id, present);
    }
  }

  /* -------------------------------------------------------------------- API */

  let hosts = null;

  function renderBasemaps(host) {
    host.textContent = '';
    for (const b of BASEMAPS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(state.basemap === b.id));
      btn.textContent = b.label;
      if (b.id !== 'osm') btn.title = `${b.label} – swisstopo, nur Schweiz`;
      btn.addEventListener('click', () => {
        state.basemap = b.id;
        for (const other of host.children) other.setAttribute('aria-checked', 'false');
        btn.setAttribute('aria-checked', 'true');
        apply();
      });
      host.append(btn);
    }
  }

  function repaint() {
    if (!hosts) return;
    renderBasemaps(hosts.basemapHost);
    renderGroups(hosts.groupHost);
    renderThemes(hosts.themeHost);
    hosts.nameInput.value = state.poiName;
    refreshCounts();
  }

  function mount(nodes) {
    hosts = nodes;
    const { basemapHost, groupHost, themeHost, nameInput, resetBtn, poiNoneBtn } = nodes;
    renderBasemaps(basemapHost);
    renderGroups(groupHost);
    renderThemes(themeHost);

    let debounce;
    nameInput.addEventListener('input', () => {
      state.poiName = nameInput.value;
      clearTimeout(debounce);
      debounce = setTimeout(apply, 180);
    });

    resetBtn.addEventListener('click', () => {
      Object.assign(state, defaultState());
      poiNoneBtn.textContent = 'Keine';
      repaint();
      apply();
    });

    poiNoneBtn.addEventListener('click', () => {
      const anyOn = THEMES.some((t) => state.poiThemes[t.id] !== false);
      for (const t of THEMES) {
        state.poiThemes[t.id] = !anyOn;
        const i = themeInputs.get(t.id);
        if (i) { i.checked = !anyOn; i.indeterminate = false; }
      }
      state.poiClasses = {};
      poiNoneBtn.textContent = anyOn ? 'Alle' : 'Keine';
      apply();
      refreshCounts();
    });

    map.on('idle', refreshCounts);

    // Das Panel steht sofort; auf die Karte wirkt es, sobald der Style da ist.
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }

  return {
    mount,
    refreshCounts,
    getState: () => structuredClone(state),
    reapply,
    setKategorie,
    setState(next) {
      if (!next) return;
      Object.assign(state, { ...defaultState(), ...next });
      // Gespeicherte Einstellungen können einen Hintergrund nennen, den es
      // nicht mehr gibt (früher hiess er 'osm' bzw. 'swisstopo').
      if (!BASEMAPS.some((b) => b.id === state.basemap)) state.basemap = 'farbig';
      repaint();
      apply();
    }
  };
}
